'use client'

import React, { memo, useCallback, useMemo } from 'react'

import {
  GoogleMap,
  InfoWindow,
  Marker,
  Polygon,
  Polyline,
  useJsApiLoader
} from '@react-google-maps/api'
import { useTranslations } from 'next-intl'
import { useTheme } from 'next-themes'

import {
  fitBoundsToMarkers,
  fitBoundsToPolygon,
  fitBoundsToPolyline
} from './boundaryUtils'
import { DefaultHtmlMarker } from './default-html-marker'
import { darkMapStyles } from './map-theme-config'
import { MapMarker, MapPolygon, MapPolyline, ReactMapProps } from './types'
import { useEnhancedInfoWindow } from './use-enhanced-info-window'

const mapContainerStyle = {
  height: '100%',
  width: '100%'
}

const defaultOptions = {
  clickableIcons: false, // Reduce unnecessary API calls
  disableDefaultUI: false,
  disableDoubleClickZoom: true,
  fullscreenControl: true,
  // Performance optimizations
  gestureHandling: 'greedy',
  mapTypeControl: true,
  optimizeWaypoints: true,
  scrollwheel: true,
  streetViewControl: true,
  suppressMarkers: false,
  zoomControl: true
}

// Memoized marker component to prevent unnecessary re-renders
const MemoizedMarker = memo<{
  marker: MapMarker
  onClick: (marker: MapMarker) => void
}>(({ marker, onClick }) => (
  <Marker
    icon={marker.icon}
    key={marker.id}
    onClick={() => onClick(marker)}
    position={marker.position}
    title={marker.title}
  />
))

// Memoized polygon component
const MemoizedPolygon = memo<{
  onClick: (polygon: MapPolygon) => void
  polygon: MapPolygon
}>(({ onClick, polygon }) => (
  <Polygon
    key={polygon.id}
    onClick={() => onClick(polygon)}
    options={{
      fillColor: polygon.fillColor || '#FF0000',
      fillOpacity: polygon.fillOpacity || 0.35,
      strokeColor: polygon.strokeColor || '#FF0000',
      strokeOpacity: polygon.strokeOpacity || 0.8,
      strokeWeight: polygon.strokeWeight || 2
    }}
    paths={polygon.paths}
  />
))

// Memoized polyline component
const MemoizedPolyline = memo<{
  onClick: (polyline: MapPolyline) => void
  polyline: MapPolyline
}>(({ onClick, polyline }) => (
  <Polyline
    key={polyline.id}
    onClick={() => onClick(polyline)}
    options={{
      strokeColor: polyline.strokeColor || '#0000FF',
      strokeOpacity: polyline.strokeOpacity || 1.0,
      strokeWeight: polyline.strokeWeight || 2
    }}
    path={polyline.path}
  />
))

const ReactMapComponentInternal: React.FC<ReactMapProps> = ({
  apiKey,
  autoSelectFirstPolyline = false,
  center = { lat: 0, lng: 0 },
  className = '',
  hideControl = false,
  markerFitOptions,
  markers = [],
  onMapClick,
  onMapReady,
  onMarkerClick,
  onPolygonClick,
  onPolylineClick,
  polygonFitOptions,
  polygons = [],
  polylineFitOptions,
  polylines = [],
  style = mapContainerStyle,
  zoom = 10
}) => {
  const { theme } = useTheme()
  const tMap = useTranslations('components.map')
  const tMapError = useTranslations('components.map.error')
  const tMapPolygon = useTranslations('components.map.polygon')

  // Use useJsApiLoader hook which handles "already loaded" case gracefully
  // It detects if script is already loaded and provides context without throwing errors
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    id: 'google-maps-script'
  })

  const [selectedMarker, setSelectedMarker] = React.useState<MapMarker | null>(
    null
  )
  const [selectedPolygon, setSelectedPolygon] =
    React.useState<MapPolygon | null>(null)
  const [, setSelectedPolyline] = React.useState<MapPolyline | null>(null)
  const [map, setMap] = React.useState<google.maps.Map | null>(null)
  const [hasAutoSelectedPolyline, setHasAutoSelectedPolyline] =
    React.useState(false)

  const mapError = loadError
    ? loadError.message || tMapError('failedToLoad')
    : null

  // Memoized event handlers to prevent unnecessary re-renders
  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const position = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
        onMapClick?.(position)
        // Clear selections when clicking on map
        setSelectedMarker(null)
        setSelectedPolygon(null)
        setSelectedPolyline(null)
      }
    },
    [onMapClick]
  )

  const handleMarkerClick = useCallback(
    (marker: MapMarker) => {
      setSelectedMarker(marker)
      setSelectedPolygon(null)
      setSelectedPolyline(null)

      // Fit map bounds to the marker if options are provided
      if (map && markerFitOptions) {
        fitBoundsToMarkers(map, [marker], markerFitOptions)
      }

      onMarkerClick?.(marker)
    },
    [map, markerFitOptions, onMarkerClick]
  )

  const handlePolygonClick = useCallback(
    (polygon: MapPolygon) => {
      setSelectedPolygon(polygon)
      setSelectedMarker(null)
      setSelectedPolyline(null)

      // Fit map bounds to the polygon using the utility function
      if (map && polygon.paths.length > 0) {
        fitBoundsToPolygon(map, polygon, polygonFitOptions)
      }

      onPolygonClick?.(polygon)
    },
    [map, polygonFitOptions, onPolygonClick]
  )

  const handlePolylineClick = useCallback(
    (polyline: MapPolyline) => {
      setSelectedPolyline(polyline)
      setSelectedMarker(null)
      setSelectedPolygon(null)

      // Fit map bounds to the polyline using the utility function
      if (map && polyline?.path?.length > 0) {
        fitBoundsToPolyline(map, polyline, polylineFitOptions)
      }

      onPolylineClick?.(polyline)
    },
    [map, polylineFitOptions, onPolylineClick]
  )

  const { handleInfoWindowLoad } = useEnhancedInfoWindow(
    Boolean(selectedMarker?.infoWindowContent),
    selectedMarker?.infoWindowContent
  )

  const handleMapLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      setMap(mapInstance)
      onMapReady?.(mapInstance)
    },
    [onMapReady]
  )

  React.useEffect(() => {
    if (
      !hasAutoSelectedPolyline &&
      autoSelectFirstPolyline &&
      polylines.length > 0 &&
      map
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- needs the loaded map instance to fitBounds imperatively
      handlePolylineClick(polylines[0])
      setHasAutoSelectedPolyline(true)
    }
  }, [
    autoSelectFirstPolyline,
    handlePolylineClick,
    hasAutoSelectedPolyline,
    map,
    polylines
  ])

  // Update map styles when theme changes
  React.useEffect(() => {
    if (map) {
      map.setOptions({
        styles: theme === 'dark' ? darkMapStyles : undefined
      })
    }
  }, [map, theme])

  // Memoize the map options to prevent unnecessary re-renders
  const mapOptions = useMemo(
    () => ({
      ...defaultOptions,
      center,
      disableDefaultUI: hideControl,
      fullscreenControl: hideControl ? false : defaultOptions.fullscreenControl,
      mapTypeControl: hideControl ? false : defaultOptions.mapTypeControl,
      streetViewControl: hideControl ? false : defaultOptions.streetViewControl,
      zoom,
      zoomControl: hideControl ? false : defaultOptions.zoomControl,
      // Apply dark styles when theme is dark
      ...(theme === 'dark' ? { styles: darkMapStyles } : {})
    }),
    [center, hideControl, zoom, theme]
  )

  // Show error state if there's a map error
  if (mapError) {
    return (
      <div
        style={{
          alignItems: 'center',
          color: 'red',
          display: 'flex',
          height: '100%',
          justifyContent: 'center',
          padding: '20px',
          textAlign: 'center'
        }}
      >
        <div>
          <h3>{tMapError('title')}</h3>
          <p>{mapError}</p>
          <p style={{ fontSize: '12px', marginTop: '10px' }}>
            {tMapError('description')}
            <br />• {tMapError('mapsApi')}
            <br />• {tMapError('directionsApi')}
          </p>
        </div>
      </div>
    )
  }

  // Render the map content (markers, polygons, polylines, etc.)
  const mapContent = (
    <GoogleMap
      center={center}
      mapContainerClassName={className}
      mapContainerStyle={style}
      onClick={handleMapClick}
      onLoad={handleMapLoad}
      options={mapOptions}
      zoom={zoom}
    >
      {/* Render Markers with memoization */}
      {markers.map((marker, index) =>
        marker.icon ? (
          <MemoizedMarker
            key={marker.id}
            marker={marker}
            onClick={handleMarkerClick}
          />
        ) : (
          <DefaultHtmlMarker
            index={index}
            key={marker.id}
            marker={{ ...marker, __isLast: index === markers.length - 1 }}
            onClick={handleMarkerClick}
          />
        )
      )}

      {/* Render Polygons with memoization */}
      {polygons.map((polygon) => (
        <MemoizedPolygon
          key={polygon.id}
          onClick={handlePolygonClick}
          polygon={polygon}
        />
      ))}

      {/* Render Polylines with memoization */}
      {polylines.map((polyline) => (
        <MemoizedPolyline
          key={polyline.id}
          onClick={handlePolylineClick}
          polyline={polyline}
        />
      ))}

      {/* Info Window for selected marker */}
      {selectedMarker && selectedMarker.infoWindowContent && (
        <InfoWindow
          onLoad={handleInfoWindowLoad}
          // onCloseClick={() => setSelectedMarker(null)}
          position={selectedMarker.position}
        >
          <>{selectedMarker.infoWindowContent}</>
        </InfoWindow>
      )}

      {/* Info Window for selected polygon */}
      {selectedPolygon && (
        <InfoWindow
          onCloseClick={() => setSelectedPolygon(null)}
          position={selectedPolygon.paths[0]} // Show info window at first point
        >
          <div>
            <h3 className="text-lg font-semibold">
              {tMapPolygon('title', { id: String(selectedPolygon.id) })}
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              {tMapPolygon('points', {
                count: String(selectedPolygon.paths.length)
              })}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              {tMapPolygon('clickToViewDetails')}
            </p>
          </div>
        </InfoWindow>
      )}

      {/* Info Window for selected polyline */}
    </GoogleMap>
  )

  // Show loading state while script is loading
  if (!isLoaded) {
    return (
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          height: '100%',
          justifyContent: 'center'
        }}
      >
        {tMap('loading')}
      </div>
    )
  }

  // Render map content - useJsApiLoader provides the necessary context
  return mapContent
}

// Export memoized component to prevent unnecessary re-renders
export const ReactMapComponent = memo(ReactMapComponentInternal)
ReactMapComponent.displayName = 'ReactMapComponent'
MemoizedPolyline.displayName = 'MemoizedPolyline'
MemoizedPolygon.displayName = 'MemoizedPolygon'
MemoizedMarker.displayName = 'MemoizedMarker'
